import { reactive, ref } from "vue";

import { useDate } from "vuetify";

import EventBus from "@/utils/eventBus";

import { useBaseComposable } from "./useBaseComposable";

import { OrderService } from "@/services";

import { isLoading } from "@/shared/base.service";

import type { OrderResponse } from "@/types/response/order";
import type { CreateOrderRequest } from "@/types/request/create/createOrder";
import type { UpdateOrderRequest } from "@/types/request/update/updateOrder";
import type { OrderDetailsResponse } from "@/types/response/orderDetails";

import type { Item } from "@/types/model/item";

export function useOrder() {
  const date = useDate();

  const { createValidationRules, minValue, required } = useBaseComposable();

  const order = ref<OrderDetailsResponse>({
    orderId: 0,
    totalAmount: "",
    user: {
      name: "",
      userId: 0,
      addresses: [],
    },
    items: [],
    history: [],
    dateReference: new Date(),
    status: "pending",
    createdAt: new Date(),
    updatedAt: new Date(),
  });
  const orders = ref<OrderResponse[]>([]);

  const columns = ref<object[]>([
    { title: "ID", align: "start", key: "orderId" },
    { title: "Cliente", align: "center", key: "user.name" },
    // { title: "Observação", align: "center", key: "observation" },
    { title: "Valor Total", align: "center", key: "totalAmount" },
    {
      title: "Criado Em",
      align: "center",
      key: "createdAt",
      value: (item: OrderResponse) =>
        date.format(item?.createdAt, "keyboardDateTime"),
    },
    {
      title: "Status",
      align: "center",
      key: "status",
      value: (item: OrderResponse) => item,
    },
    {
      title: "Ações",
      align: "center",
      key: "actions",
      sortable: false,
      value: (item: OrderResponse) => item,
    },
  ]);

  const newOrderColumns = ref<object[]>([
    {
      title: "Produto",
      align: "start",
      key: "name",
      sortable: false,
      value: (item: Item) => item,
    },
    {
      title: "Preço",
      align: "center",
      key: "price",
      sortable: false,
      value: (item: Item) => item,
    },
    { title: "Quantidade", align: "center", key: "quantity", sortable: false },
    {
      title: "Observação",
      align: "center",
      key: "observation",
      sortable: false,
      value: (item: Item) => item,
    },
    {
      title: "Total",
      align: "center",
      key: "total",
      sortable: false,
      value: (item: Item) => item,
    },

    {
      title: "Ação",
      align: "center",
      key: "actions",
      sortable: false,
      value: (item: OrderResponse) => item,
    },
  ]);

  const pagination = ref({
    totalElements: 0,
    currentPage: 1,
    totalPerPage: 10,
    totalPages: 1,
  });

  const newOrderItemFormRules = createValidationRules({
    itemId: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minValue: {
        validator: minValue(1),
        messageKey: "validation.minValue",
        params: { min: 1 },
      },
    },
    quantity: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      minValue: {
        validator: minValue(1),
        messageKey: "validation.minValue",
        params: { min: 1 },
      },
    },
  });

  const newOrderItemForm = ref<Item>({
    itemId: 0,
    name: "",
    description: "",
    observation: "",
    quantity: 1,
    price: 0.0,
  });

  const newOrderFormRules = createValidationRules({
    user: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
    },
    userId: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
      // minValue: {
      //   validator: minValue(1),
      //   messageKey: "validation.minValue",
      //   params: { min: 1 },
      // },
    },
    totalAmount: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
    },
    items: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
    },
  });

  const newOrderForm = ref<CreateOrderRequest>({
    address: undefined,
    user: undefined,
    userId: undefined,
    totalAmount: 0,
    items: [],
    dateReference: new Date().toISOString().split("T")[0],
  });

  const updateOrderFormRules = createValidationRules({
    status: {
      required: {
        validator: required,
        messageKey: "validation.required",
      },
    },
  });

  const updateOrderForm = reactive<UpdateOrderRequest>({
    orderId: 0,
    status: "pending",
  });

  const createNewOrderItemForm = (): Item => ({
    itemId: 0,
    quantity: 1,
    observation: "",
    name: "",
    price: 0,
    description: "",
  });

  async function getAllOrders(currentPage: number, totalElements: number) {
    try {
      const response = await OrderService.GetAllOrders(
        currentPage,
        totalElements
      );

      if (response) {
        orders.value = response.data;

        pagination.value = {
          totalElements: response.totalElements,
          currentPage: response.currentPage,
          totalPerPage: response.totalPerPage,
          totalPages: response.totalPages,
        };
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function getOrderById(id: number) {
    try {
      const response = await OrderService.GetOrderById(id);

      if (response) order.value = response;

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function createOrder(payload: CreateOrderRequest) {
    try {
      const response = await OrderService.CreateOrder(payload);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function updateOrder(id: number, payload: UpdateOrderRequest) {
    try {
      const response = await OrderService.UpdateOrder(id, payload);

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async function deleteOrderById(id: number) {
    try {
      const response = await OrderService.DeleteOrderById(id);

      if (response) EventBus.emit("Order-update");

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  return {
    isLoading,
    columns,
    newOrderColumns,
    pagination,
    order,
    orders,
    newOrderItemFormRules,
    newOrderItemForm,
    newOrderFormRules,
    newOrderForm,
    updateOrderFormRules,
    updateOrderForm,
    createNewOrderItemForm,
    getAllOrders,
    getOrderById,
    createOrder,
    updateOrder,
    deleteOrderById,
  };
}
